import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimMagicUIBorderBeam } from '@readdy/anim/card/magic-ui-border-beam/react';
import Logo from './Logo';

interface DropdownItem {
  label: string;
  path: string;
  description?: string;
}

interface NavItem {
  label: string;
  path: string;
  dropdown?: DropdownItem[];
}

const navItems: NavItem[] = [
  { path: '/', label: 'Home' },
  {
    label: 'Company',
    path: '/about',
    dropdown: [
      { label: 'Founder & Chairman', path: '/founder-message', description: 'A message from our leadership' },
      { label: 'About Us', path: '/about', description: 'Our story & leadership' },
      { label: 'Mission & Vision', path: '/mission-vision', description: 'What drives us forward' },
      { label: 'Investment', path: '/investment', description: 'Investor relations' },
      { label: 'Partnership', path: '/agents-request', description: 'Partner with TMT' },
      { label: 'Careers', path: '/careers', description: 'Join our global team' },
    ],
  },
  {
    label: 'Services',
    path: '/services',
    dropdown: [
      { label: 'Money Transfer', path: '/services/money-transfer', description: 'Fast cross-border transfers' },
      { label: 'P2P & Payroll', path: '/services/p2p-payroll', description: 'Instant peer-to-peer payments' },
      { label: 'Visa Cards', path: '/services/visa-cards', description: 'Digital & physical cards' },
      { label: 'Bill Payments', path: '/services/bills-merchant', description: 'Pay bills & merchants' },
    ],
  },
  {
    label: 'Security',
    path: '/security',
    dropdown: [
      { label: 'Security Overview', path: '/security', description: 'Our security framework' },
      { label: 'Privacy Policy', path: '/privacy', description: 'How we protect your data' },
      { label: 'Terms of Service', path: '/terms', description: 'Rules & agreements' },
      { label: 'Cookie Policy', path: '/cookies', description: 'Cookie usage details' },
    ],
  },
  { path: '/support', label: 'Support' },
  {
    label: 'Contact',
    path: '/contact',
    dropdown: [
      { label: '24/7 Chat with Us', path: '#chat', description: 'Get instant support via live chat' },
      { label: 'Send Us a Message', path: '#contact-form', description: 'Reach out via our contact form' },
    ],
  },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  const isParentActive = (item: NavItem): boolean => {
    if (location.pathname === item.path) return true;
    if (item.dropdown) {
      return item.dropdown.some((d) => d.path !== '#contact-form' && location.pathname === d.path);
    }
    return false;
  };

  const scrollToDownload = () => {
    // Skip the landing overlay so user sees the download section immediately
    sessionStorage.setItem('tmt-landing-seen', 'true');

    if (location.pathname === '/') {
      const el = document.getElementById('download-app');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Use hash to trigger immediate scroll on destination page
      navigate('/#download-app');
      setTimeout(() => {
        const el = document.getElementById('download-app');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  };

  const scrollToContactForm = () => {
    if (location.pathname === '/contact') {
      const el = document.getElementById('contact-form');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/contact');
      setTimeout(() => {
        const el = document.getElementById('contact-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    }
  };

  const openChat = () => {
    const chatBtn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (chatBtn) {
      chatBtn.click();
    }
  };

  return (
    <header className="relative z-50 bg-transparent">
      <nav className="w-full px-3 lg:px-6" ref={navRef}>
        <div className="flex items-center justify-between min-h-[56px] lg:min-h-[64px] py-1.5 lg:py-2">
          {/* Logo */}
          <Link to="/" className="flex items-center group shrink-0">
            {reducedMotion ? (
              <Logo size="lg" showSlogan={true} linkToHome={false} />
            ) : (
              <AnimMagicUIBorderBeam duration={3000} borderWidth={1.35} glowOpacity={0.28}>
                <Logo size="lg" showSlogan={true} linkToHome={false} />
              </AnimMagicUIBorderBeam>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {/* Download App — scrolls to mobile app section */}
            <button
              onClick={scrollToDownload}
              className="font-label text-[0.65rem] lg:text-xs tracking-widest uppercase transition-colors duration-300 whitespace-nowrap px-2 lg:px-3 py-2 rounded-md cursor-pointer text-foreground-200 hover:text-primary-400"
            >
              Download App
            </button>

            {navItems.map((item) => (
              <div
                key={item.label}
                className={`relative ${item.dropdown ? 'group' : ''}`}
              >
                {item.dropdown ? (
                  <>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-1 font-label text-[0.65rem] lg:text-xs tracking-widest uppercase transition-colors duration-300 whitespace-nowrap px-2 lg:px-3 py-2 rounded-md cursor-pointer ${
                        isParentActive(item)
                          ? 'text-primary-400'
                          : 'text-foreground-200 hover:text-primary-400'
                      }`}
                    >
                      {item.label}
                      <i className="ri-arrow-down-s-line text-xs transition-transform duration-300 group-hover:rotate-180" />
                    </Link>

                    {/* Hover bridge — invisible div that keeps hover alive between button and dropdown */}
                    <div className="absolute top-full left-0 right-0 h-5 bg-transparent pointer-events-auto" />

                    {/* Dropdown Panel */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 border border-primary-700/20 rounded-xl shadow-lg overflow-hidden transition-all duration-300 origin-top opacity-0 scale-95 -translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
                      <div className="py-2 px-1">
                        {item.dropdown.map((sub) =>
                          sub.path === '#chat' ? (
                            <button
                              key={sub.label}
                              onClick={() => openChat()}
                              className="w-full text-left flex flex-col px-4 py-3 rounded-lg transition-all duration-200 group/item hover:bg-background-100 cursor-pointer"
                            >
                              <span className="font-label text-xs font-semibold tracking-wider uppercase transition-colors text-foreground-100 group-hover/item:text-primary-400">
                                {sub.label}
                              </span>
                              {sub.description && (
                                <span className="text-foreground-500 text-[0.65rem] mt-0.5 font-body">
                                  {sub.description}
                                </span>
                              )}
                            </button>
                          ) : sub.path === '#contact-form' ? (
                            <button
                              key={sub.label}
                              onClick={() => scrollToContactForm()}
                              className="w-full text-left flex flex-col px-4 py-3 rounded-lg transition-all duration-200 group/item hover:bg-background-100 cursor-pointer"
                            >
                              <span className="font-label text-xs font-semibold tracking-wider uppercase transition-colors text-foreground-100 group-hover/item:text-primary-400">
                                {sub.label}
                              </span>
                              {sub.description && (
                                <span className="text-foreground-500 text-[0.65rem] mt-0.5 font-body">
                                  {sub.description}
                                </span>
                              )}
                            </button>
                          ) : (
                            <Link
                              key={sub.label}
                              to={sub.path}
                              className={`flex flex-col px-4 py-3 rounded-lg transition-all duration-200 group/item ${
                                location.pathname === sub.path
                                  ? 'bg-primary-500/10'
                                  : 'hover:bg-background-100'
                              }`}
                            >
                              <span
                                className={`font-label text-xs font-semibold tracking-wider uppercase transition-colors ${
                                  location.pathname === sub.path
                                    ? 'text-primary-400'
                                    : 'text-foreground-100 group-hover/item:text-primary-400'
                                }`}
                              >
                                {sub.label}
                              </span>
                              {sub.description && (
                                <span className="text-foreground-500 text-[0.65rem] mt-0.5 font-body">
                                  {sub.description}
                                </span>
                              )}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`font-label text-[0.65rem] lg:text-xs tracking-widest uppercase transition-colors duration-300 whitespace-nowrap px-2 lg:px-3 py-2 ${
                      location.pathname === item.path
                        ? 'text-primary-400'
                        : 'text-foreground-200 hover:text-primary-400'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}

            {/* Divider */}
            <div className="h-4 w-px bg-primary-700/40 mx-2 lg:mx-3" />

            {/* Auth Buttons */}
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="font-label text-[0.7rem] lg:text-xs tracking-wider px-3 lg:px-4 py-1.5 lg:py-2 border border-primary-500 text-primary-400 rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-300 whitespace-nowrap"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="font-label text-[0.7rem] lg:text-xs tracking-wider px-3 lg:px-4 py-1.5 lg:py-2 bg-primary-500 text-background-50 rounded-full hover:bg-primary-400 transition-all duration-300 whitespace-nowrap"
              >
                Open Account
              </Link>
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50 border-t border-primary-700/30 px-4 py-6 space-y-1 overflow-y-auto max-h-[calc(100vh-56px)]">
          {/* Download App — mobile */}
          <button
            onClick={() => { setMobileOpen(false); setMobileExpanded(null); scrollToDownload(); }}
            className="w-full text-left font-label text-sm tracking-widest uppercase py-3 text-foreground-200 hover:text-primary-400 transition-colors"
          >
            Download App
          </button>

          {navItems.map((item) => (
            <div key={item.label}>
              {item.dropdown ? (
                <>
                  <button
                    className="w-full flex items-center justify-between font-label text-sm tracking-widest uppercase py-3 text-foreground-200 transition-colors"
                    onClick={() =>
                      setMobileExpanded(mobileExpanded === item.label ? null : item.label)
                    }
                  >
                    <span className={isParentActive(item) ? 'text-primary-400' : ''}>
                      {item.label}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line transition-transform duration-300 ${
                        mobileExpanded === item.label ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-400 ${
                      mobileExpanded === item.label ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pl-4 py-1 space-y-1 border-l border-primary-700/20 ml-2">
                      {item.dropdown.map((sub) =>
                        sub.path === '#chat' ? (
                          <button
                            key={sub.label}
                            onClick={() => { setMobileOpen(false); setMobileExpanded(null); openChat(); }}
                            className="block w-full text-left py-2.5 text-sm font-body text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer"
                          >
                            {sub.label}
                          </button>
                        ) : sub.path === '#contact-form' ? (
                          <button
                            key={sub.label}
                            onClick={() => { setMobileOpen(false); setMobileExpanded(null); scrollToContactForm(); }}
                            className="block w-full text-left py-2.5 text-sm font-body text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer"
                          >
                            {sub.label}
                          </button>
                        ) : (
                          <Link
                            key={sub.label}
                            to={sub.path}
                            onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                            className={`block py-2.5 text-sm font-body transition-colors ${
                              location.pathname === sub.path
                                ? 'text-primary-400'
                                : 'text-foreground-400 hover:text-primary-400'
                            }`}
                          >
                            {sub.label}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                  className={`block font-label text-sm tracking-widest uppercase py-3 transition-colors ${
                    location.pathname === item.path
                      ? 'text-primary-400'
                      : 'text-foreground-200'
                  }`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="pt-4 mt-2 border-t border-primary-700/30 flex flex-col gap-3">
            <Link
              to="/login"
              onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
              className="font-label text-sm tracking-wider px-5 py-3 border border-primary-500 text-primary-400 rounded-full text-center"
            >
              Log In
            </Link>
            <Link
              to="/signup"
              onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
              className="font-label text-sm tracking-wider px-5 py-3 bg-primary-500 text-background-50 rounded-full text-center"
            >
              Open Account
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}